import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Quote, Loader2, CheckCircle2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { SectionHead } from "./SectionHead";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewForm = ({ onDone }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ name: "", comment: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please pick a star rating."); return; }
    if (!form.name || !form.comment) { toast.error("Please add your name and a short comment."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { name: form.name, rating, comment: form.comment });
      setDone(true);
      onDone?.();
    } catch {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="home-review-success">
        <CheckCircle2 className="text-[#F2B705] mx-auto" size={48} />
        <h3 className="font-display font-black text-2xl mt-4">Thank You! 🙏</h3>
        <p className="text-white/60 mt-2">Your review was submitted and will appear here once approved.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-2 space-y-6" data-testid="home-review-form">
      <div className="flex justify-center gap-2" data-testid="home-review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`home-review-star-${n}`}
            onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
            className="transition-transform hover:scale-110">
            <Star size={34} className={(hover || rating) >= n ? "text-[#F2B705] fill-[#F2B705]" : "text-white/25"} />
          </button>
        ))}
      </div>
      <input data-testid="home-review-name" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 rounded-none placeholder:text-white/30" placeholder="Your name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
      <textarea data-testid="home-review-comment" rows={4} className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-3 rounded-none placeholder:text-white/30" placeholder="Tell us about your rental... *" value={form.comment} onChange={(e) => set("comment", e.target.value)} />
      <button data-testid="home-review-submit" type="submit" disabled={loading}
        className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
        {loading && <Loader2 className="animate-spin" size={18} />} {loading ? "Submitting..." : "Submit Review"}
      </button>
    </form>
  );
};

export const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews/approved`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  const LeaveReviewButton = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="leave-review-btn"
          className="inline-flex items-center gap-2 bg-[#F2B705] text-black font-bold px-7 py-3.5 rounded-md hover:bg-white transition-colors">
          <PenLine size={18} /> Leave a Review
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#0C0C0C] border-white/10 text-white sm:max-w-md" data-testid="leave-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl">Leave a Review</DialogTitle>
        </DialogHeader>
        <p className="text-white/55 text-sm -mt-1">How was your experience with Auto-Hire STL? Your feedback means the world to us.</p>
        <ReviewForm onDone={() => setTimeout(() => setOpen(false), 2200)} />
      </DialogContent>
    </Dialog>
  );

  return (
    <section id="reviews" data-testid="testimonials-section" className="relative py-24 md:py-32 border-t border-white/5 bg-[#080808]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionHead overline="Reviews" title="What Drivers" accent="Say." desc="Real words from real Auto-Hire STL customers." />

        {reviews.length === 0 ? (
          <div className="text-center mt-14 max-w-xl mx-auto" data-testid="reviews-empty">
            <Quote className="text-[#F2B705]/40 mx-auto" size={40} />
            <p className="text-white/65 text-lg mt-5 font-light">No reviews yet — be the first to share your experience.</p>
            <div className="mt-8">{LeaveReviewButton}</div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
              {reviews.slice(0, 9).map((r, i) => (
                <motion.div
                  key={r.id}
                  data-testid={`testimonial-${r.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  className="bg-[#0C0C0C] border border-white/10 rounded-xl p-6 hover:border-[#F2B705]/40 transition-colors"
                >
                  <Quote className="text-[#F2B705]/40" size={26} />
                  <div className="flex gap-1 mt-3">
                    {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={16} className={n <= r.rating ? "text-[#F2B705] fill-[#F2B705]" : "text-white/20"} />)}
                  </div>
                  <p className="text-white/75 mt-4 font-light leading-relaxed">"{r.comment}"</p>
                  <div className="mt-5 font-display font-bold">{r.name}</div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-14">{LeaveReviewButton}</div>
          </>
        )}
      </div>
    </section>
  );
};
